import { getRouteHeadingDegrees } from '../Home/map/monitorMapEffects';
import {
  shapeMonitorRoute,
  wrapLngNear,
  type LngLat,
} from '../Home/map/monitorRouteShape';

type LatLng = { lat: number; lng: number };

/**
 * The spotlight's line on the /monitor globe:
 *
 * - Not picked up yet: dashed from origin to destination; no arrow on the
 *   circle (arrowheads glide along the dashes, slower and fainter: see
 *   routeArrows).
 * - On its way: solid where it has been (origin, then its scans and sensor
 *   track, to where it is now), dashed straight on to the destination; the
 *   arrow points along the dashed part.
 * - Origin or destination unknown: no line.
 *
 * Long legs bend the short way round the globe; the circle is always on the
 * shipment, whatever the line.
 */
export interface MonitorTripLine {
  /** The shipment it belongs to: a new key draws itself in. */
  key: string;
  /** Solid. One point (the start) when nothing has been travelled. */
  traveled: LngLat[];
  /** Dashed, ending at the destination. */
  remaining: LngLat[];
  /** Picked up: the line splits at the shipment and the arrow shows. */
  moving: boolean;
  /** Direction of travel (degrees from north), or null for no arrow. */
  heading: number | null;
  /**
   * The dashed part as positions, for "On this route". It starts at the
   * shipment only when `moving`; before pickup it is origin to destination.
   */
  lane: LatLng[];
  /**
   * What the camera frames: the whole line and the shipment, which before
   * pickup can be somewhere else (a scan far from the origin).
   */
  frame: LngLat[];
}

/** A place whose coordinates may be missing (a trip's origin, say). */
type MaybePlace = { lat?: number | null; lng?: number | null };

const isPlace = (p: MaybePlace | null | undefined): p is LatLng =>
  typeof p?.lat === 'number' &&
  typeof p?.lng === 'number' &&
  Number.isFinite(p.lat) &&
  Number.isFinite(p.lng) &&
  !(p.lat === 0 && p.lng === 0);

const toLngLat = ({ lat, lng }: LatLng): LngLat => [lng, lat];

/** Two fixes this close are the same place (about 10 m). */
const samePlace = (a: LngLat, b: LngLat) =>
  Math.abs(a[0] - b[0]) < 1e-4 && Math.abs(a[1] - b[1]) < 1e-4;

export const monitorTripLine = ({
  key,
  origin,
  destination,
  path,
  pickedUp,
  position,
}: {
  key: string;
  origin: MaybePlace | null | undefined;
  destination: MaybePlace | null | undefined;
  /** Where it has been since pickup, starting at the origin (trip.path). */
  path: MaybePlace[] | null | undefined;
  pickedUp: boolean;
  /** Where it is now, as the circle shows it (sensor or scan). */
  position: LatLng | null;
}): MonitorTripLine | null => {
  if (!isPlace(origin) || !isPlace(destination)) return null;
  const end = toLngLat(destination);

  if (!pickedUp) {
    const { traveled, remaining } = shapeMonitorRoute(
      [toLngLat(origin), end],
      0,
      position?.lng ?? null
    );
    const frame: LngLat[] = [...remaining];
    if (isPlace(position))
      frame.push([wrapLngNear(position.lng, remaining[0][0]), position.lat]);
    return {
      key,
      traveled,
      remaining,
      moving: false,
      heading: null,
      lane: remaining.map(([lng, lat]) => ({ lat, lng })),
      frame,
    };
  }

  const history = (path ?? []).filter(isPlace).map(toLngLat);
  if (history.length === 0) history.push(toLngLat(origin));
  const last = history[history.length - 1];
  const here = isPlace(position) ? toLngLat(position) : last;
  const points = samePlace(last, here) ? history : [...history, here];
  const { traveled, remaining } = shapeMonitorRoute(
    [...points, end],
    points.length - 1,
    here[0]
  );
  const lane = remaining.map(([lng, lat]) => ({ lat, lng }));
  const heading = getRouteHeadingDegrees(
    lane.map(({ lat, lng }) => ({ lat, long: lng })),
    0,
    { lat: here[1], lng: here[0] }
  );
  // The line runs through the shipment, so it frames it too.
  const frame = [...traveled, ...remaining.slice(1)];
  return { key, traveled, remaining, moving: true, heading, lane, frame };
};
